import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Input, Select } from "Components/Form";
import { useTranslation } from "react-i18next";
import {
  AddTriggerButton,
  BlankText,
  CloseIcon,
  EvidencesChip,
  LogicActionsWrapper,
  LogicContains,
  LogicQuestionsSection,
  LogicSelection,
  RemoveLogicButton,
  QuestionsChip,
} from "./styles";
import { Colors } from "Components/Form/Button";
import { Add, Trash } from "iconsax-react";
import { LogicQuestions } from "..";
import { Option, QuestionLogics, Questions } from "Models/checklist";
import { StyledMenu } from "Components/Generic/AnchorMenu";
import { EvidencesDrawer, EvidencesDrawerHandles } from "../EvidencesDrawer";
import {
  MultipleLogicOption,
  NumberLogicOption,
  SingleLogicOption,
  Status,
  TextLogicOption,
} from "Util/enums";
import {
  ChecklistContext,
  MAXIMUM_QUESTIONS_DEPTH,
  MAXIMUM_QUESTIONS_PER_LOGIC,
} from "../../../context";
import { findLogicPath, QuestionTypes, useLogicErrors } from "../../../../utils";
import { ConfirmDeleteLogicModal, DeleteLogicHandle } from "../ConfirmDeleteLogicModal";

interface LogicActionsProps {
  questionType: string;
  isRequiredQuestion: boolean;
  isOnViewMode: boolean;
  questionLogic: QuestionLogics;
  singleOrMultipleOptions?: Option[];
}

export const LogicActions = ({
  questionType,
  isRequiredQuestion,
  isOnViewMode,
  questionLogic,
  singleOrMultipleOptions,
}: LogicActionsProps) => {
  const { t: translate } = useTranslation();
  const {
    checklist,
    errors,
    handleRemoveQuestion,
    handleRemoveQuestionLogics,
    handleUpdateQuestionLogics,
    handleUpdateMultipleFieldsInQuestionLogic,
  } = useContext(ChecklistContext);

  const [logic, setLogic] = useState<QuestionLogics>(questionLogic);
  const [triggerAnchorElement, setTriggerAnchorElement] = useState<HTMLElement | null>(null);
  const [typingTimeout, setTypingTimeout] = useState<any>(0);

  const evidencesDrawerRef = useRef<EvidencesDrawerHandles>(null);
  const deleteLogicModalRef = useRef<DeleteLogicHandle>(null);

  const logicPath = useMemo(() => {
    return findLogicPath({
      questions: checklist?.questions,
      logicId: logic.id,
      logicTemporaryId: logic.temporaryId,
    });
  }, [checklist?.questions, logic.id, logic.temporaryId]);

  const { logicConditionError, logicValueError } = useLogicErrors({ errors, logicPath });

  const handleUpdateLogicFields = (attribute: keyof QuestionLogics, value: any) => {
    let logicToUpdate = { ...logic, [attribute]: value };

    // handling logic value for blank
    if (attribute === "logicCondition") {
      if (
        value === TextLogicOption.IS ||
        value === TextLogicOption.IS_NOT ||
        value === NumberLogicOption.IS ||
        value === NumberLogicOption.IS_NOT
      ) {
        logicToUpdate = { ...logic, [attribute]: value, logicValue: "blank" };
      } else {
        logicToUpdate = { ...logic, [attribute]: value, logicValue: "" };
      }
    }

    setLogic(logicToUpdate);
  };

  const logicOptions = useMemo(() => {
    const options = [];

    switch (questionType) {
      case QuestionTypes.TEXT:
        options.push({ label: translate("contains"), value: TextLogicOption.CONTAINS });
        if (!isRequiredQuestion) {
          options.unshift(
            { label: translate("is"), value: TextLogicOption.IS },
            { label: translate("isNot"), value: TextLogicOption.IS_NOT }
          );
        }
        break;
      case QuestionTypes.NUMBER:
        options.push(
          { label: translate("isLesserThan"), value: NumberLogicOption.IS_LESSER_THAN },
          { label: translate("isGreaterThan"), value: NumberLogicOption.IS_GREATER_THAN }
        );
        if (!isRequiredQuestion) {
          options.unshift(
            { label: translate("is"), value: NumberLogicOption.IS },
            { label: translate("isNot"), value: NumberLogicOption.IS_NOT }
          );
        }
        break;
      case QuestionTypes.SINGLE:
        options.push(
          { label: translate("is"), value: SingleLogicOption.IS },
          { label: translate("isNot"), value: SingleLogicOption.IS_NOT }
        );
        break;
      case QuestionTypes.MULTIPLE:
        options.push(
          { label: translate("selected"), value: MultipleLogicOption.SELECTED },
          { label: translate("notSelected"), value: MultipleLogicOption.NOT_SELECTED }
        );
        break;
      default:
        break;
    }

    return options;
  }, [questionType, isRequiredQuestion]);

  useEffect(() => {
    setLogic(questionLogic);
  }, [questionLogic]);

  const shouldDisableAddingLogicQuestions = (
    questions: Questions[],
    logicItem: QuestionLogics
  ): boolean => {
    const calculateLogicDepth = (currentDepth: number, currentQuestions?: Questions[]): number => {
      if (!currentQuestions) return 0;

      for (const question of currentQuestions) {
        if (question.questionLogics) {
          for (const logic of question.questionLogics) {
            if (
              (logic.id && logic.id === logicItem.id) ||
              (logic.temporaryId && logic.temporaryId === logicItem.temporaryId)
            ) {
              return currentDepth; // Found the logic, return depth
            }
            // Recurse into nested questions
            const depth = calculateLogicDepth(currentDepth + 1, logic.questions);

            if (depth) return depth;
          }
        }
      }

      return 0;
    };

    // Count only non-evidence questions
    const nonEvidenceQuestionsCount = (logicItem.questions || []).filter(
      (question) => !question.isEvidence
    ).length;

    return (
      nonEvidenceQuestionsCount >= MAXIMUM_QUESTIONS_PER_LOGIC ||
      calculateLogicDepth(1, questions) >= MAXIMUM_QUESTIONS_DEPTH
    );
  };

  const hasEvidenceTrigger = logic.isEvidenceNotesRequired || logic.isEvidencePhotosRequired;

  const removeEvidenceQuestions = () => {
    const filteredQuestions = (logic.questions || []).filter((question) => !question.isEvidence);

    handleUpdateMultipleFieldsInQuestionLogic({
      fields: {
        isEvidenceNotesRequired: false,
        isEvidencePhotosRequired: false,
        questions: filteredQuestions,
      },
      logicTemporaryId: logic.temporaryId,
      questionLogicId: logic.id,
    });

    setLogic({
      ...logic,
      isEvidenceNotesRequired: false,
      isEvidencePhotosRequired: false,
      questions: filteredQuestions,
    });
  };

  const renderInputOrBlank = () => {
    switch (questionType) {
      case QuestionTypes.TEXT:
        if (
          logic.logicCondition === TextLogicOption.IS ||
          logic.logicCondition === TextLogicOption.IS_NOT
        ) {
          return <BlankText>{translate("blank")}</BlankText>;
        }

        return (
          <LogicContains>
            <Input
              value={logic.logicValue}
              disabled={isOnViewMode}
              error={!!logicValueError}
              errorMessage={logicValueError}
              placeholder={translate("enterText")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (typingTimeout) clearTimeout(typingTimeout);

                handleUpdateLogicFields("logicValue", event.target.value);

                setTypingTimeout(
                  setTimeout(
                    () =>
                      handleUpdateQuestionLogics({
                        value: event.target.value,
                        field: "logicValue",
                        logicTemporaryId: logic.temporaryId,
                        questionLogicId: logic.id,
                      }),
                    500
                  )
                );
              }}
            />
          </LogicContains>
        );
      case QuestionTypes.NUMBER:
        if (
          logic.logicCondition === NumberLogicOption.IS ||
          logic.logicCondition === NumberLogicOption.IS_NOT
        ) {
          return <BlankText>{translate("blank")}</BlankText>;
        }

        return (
          <LogicContains>
            <Input
              value={logic.logicValue}
              disabled={isOnViewMode}
              error={!!logicValueError}
              errorMessage={logicValueError}
              type="number"
              placeholder={translate("enterText")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (typingTimeout) clearTimeout(typingTimeout);

                handleUpdateLogicFields("logicValue", event.target.value);

                setTypingTimeout(
                  setTimeout(
                    () =>
                      handleUpdateQuestionLogics({
                        value: event.target.value,
                        field: "logicValue",
                        logicTemporaryId: logic.temporaryId,
                        questionLogicId: logic.id,
                      }),
                    500
                  )
                );
              }}
            />
          </LogicContains>
        );
      case QuestionTypes.SINGLE:
        return (
          <LogicContains>
            <Select
              value={logic.logicValue}
              disabled={isOnViewMode}
              error={!!logicValueError}
              errorMessage={logicValueError}
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateLogicFields("logicValue", event.target.value);
                handleUpdateQuestionLogics({
                  value: event.target.value,
                  field: "logicValue",
                  logicTemporaryId: logic.temporaryId,
                  questionLogicId: logic.id,
                });
              }}
              placeholder={translate("selectOption")}
            >
              {singleOrMultipleOptions?.map((option, index) => (
                <MenuItem value={option.name} key={index}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </LogicContains>
        );
      default:
        return;
    }
  };

  return (
    <>
      {questionType === QuestionTypes.MULTIPLE ? (
        <LogicActionsWrapper key={`${logic.id || logic.temporaryId}`}>
          <span>{translate("if")}</span>

          <LogicContains>
            <Select
              value={logic.logicValue}
              disabled={isOnViewMode}
              error={!!logicValueError}
              errorMessage={logicValueError}
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateLogicFields("logicValue", event.target.value);
                handleUpdateQuestionLogics({
                  value: event.target.value,
                  field: "logicValue",
                  logicTemporaryId: logic.temporaryId,
                  questionLogicId: logic.id,
                });
              }}
              placeholder={translate("selectOption")}
            >
              {singleOrMultipleOptions?.map((option, index) => (
                <MenuItem value={option.name} key={index}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </LogicContains>

          <span>{translate("is")}</span>

          <LogicSelection>
            <Select
              value={logic.logicCondition}
              disabled={isOnViewMode}
              error={!!logicConditionError}
              errorMessage={logicConditionError}
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateLogicFields("logicCondition", event.target.value);
                handleUpdateQuestionLogics({
                  value: event.target.value,
                  field: "logicCondition",
                  logicTemporaryId: logic.temporaryId,
                  questionLogicId: logic.id,
                });
              }}
            >
              {logicOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </LogicSelection>

          <span>{translate("then")}</span>

          {logic.questions
            ?.filter((question) => !question.isEvidence)
            ?.map((question, index) => (
              <QuestionsChip key={index}>
                {translate("askQuestion")}
                <CloseIcon
                  fontSize="inherit"
                  disabled={isOnViewMode}
                  onClick={() =>
                    isOnViewMode
                      ? null
                      : handleRemoveQuestion({
                          questionId: question.id,
                          temporaryId: question.temporaryId,
                        })
                  }
                />
              </QuestionsChip>
            ))}

          {hasEvidenceTrigger && (
            <EvidencesChip>
              {translate("requireEvidence")}
              <CloseIcon
                fontSize="inherit"
                disabled={isOnViewMode}
                onClick={() => (isOnViewMode ? null : removeEvidenceQuestions())}
              />
            </EvidencesChip>
          )}

          <AddTriggerButton
            customColor={Colors.SECONDARY_DARK_OUTLINED}
            startIcon={<Add size={18} />}
            disabled={isOnViewMode}
            onClick={(event) => {
              setTriggerAnchorElement(event.currentTarget);
            }}
          >
            {translate("trigger")}
          </AddTriggerButton>

          <RemoveLogicButton
            onClick={() => deleteLogicModalRef.current?.onOpenModal()}
            disabled={isOnViewMode}
          >
            <Trash />
          </RemoveLogicButton>
        </LogicActionsWrapper>
      ) : (
        <LogicActionsWrapper key={`${logic.id || logic.temporaryId}`}>
          <span>{translate("ifAnswer")}</span>

          <LogicSelection>
            <Select
              value={logic.logicCondition}
              disabled={isOnViewMode}
              error={!!logicConditionError}
              errorMessage={logicConditionError}
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateLogicFields("logicCondition", event.target.value);
                handleUpdateQuestionLogics({
                  value: event.target.value,
                  field: "logicCondition",
                  logicTemporaryId: logic.temporaryId,
                  questionLogicId: logic.id,
                });
              }}
            >
              {logicOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </LogicSelection>

          {renderInputOrBlank()}

          <span>{translate("then")}</span>

          {logic.questions
            ?.filter((question) => !question.isEvidence)
            ?.map((question, index) => (
              <QuestionsChip key={index}>
                {translate("askQuestion")}
                <CloseIcon
                  fontSize="inherit"
                  disabled={isOnViewMode}
                  onClick={() =>
                    isOnViewMode
                      ? null
                      : handleRemoveQuestion({
                          questionId: question.id,
                          temporaryId: question.temporaryId,
                        })
                  }
                />
              </QuestionsChip>
            ))}

          {hasEvidenceTrigger && (
            <EvidencesChip>
              {translate("requireEvidence")}
              <CloseIcon
                fontSize="inherit"
                disabled={isOnViewMode}
                onClick={() => (isOnViewMode ? null : removeEvidenceQuestions())}
              />
            </EvidencesChip>
          )}

          <AddTriggerButton
            customColor={Colors.SECONDARY_DARK_OUTLINED}
            startIcon={<Add size={18} />}
            disabled={isOnViewMode}
            onClick={(event) => {
              setTriggerAnchorElement(event.currentTarget);
            }}
          >
            {translate("trigger")}
          </AddTriggerButton>

          <RemoveLogicButton
            onClick={() => deleteLogicModalRef.current?.onOpenModal()}
            disabled={isOnViewMode}
          >
            <Trash />
          </RemoveLogicButton>
        </LogicActionsWrapper>
      )}

      <StyledMenu
        anchorEl={triggerAnchorElement}
        open={!!triggerAnchorElement}
        onClose={() => setTriggerAnchorElement(null)}
      >
        <MenuItem
          onClick={() => {
            setTriggerAnchorElement(null);
            evidencesDrawerRef.current?.open(logic);
          }}
        >
          {translate("requireEvidence")}
        </MenuItem>
        <MenuItem
          disabled={shouldDisableAddingLogicQuestions(checklist?.questions || [], logic)}
          onClick={() => {
            setTriggerAnchorElement(null);

            const questionTemporaryId = Math.random().toString(36).substring(2, 15);

            handleUpdateLogicFields("questions", [
              ...(logic.questions ? logic.questions : []),
              {
                answerAction: "",
                description: "",
                mandatory: true,
                needAction: false,
                questionOrder: (logic.questions || []).length + 1,
                status: Status.ACTIVE,
                title: "",
                type: QuestionTypes.TEXT,
                temporaryId: questionTemporaryId,
                questionLogics: [],
                ...(logic.id ? { logicId: logic.id } : {}),
              },
            ]);

            handleUpdateQuestionLogics({
              value: [
                ...(logic.questions ? logic.questions : []),
                {
                  answerAction: "",
                  description: "",
                  mandatory: true,
                  needAction: false,
                  questionOrder: (logic.questions || []).length + 1,
                  status: Status.ACTIVE,
                  title: "",
                  type: QuestionTypes.TEXT,
                  temporaryId: questionTemporaryId,
                  questionLogics: [],
                  ...(logic.id ? { logicId: logic.id } : {}),
                },
              ],
              field: "questions",
              logicTemporaryId: logic.temporaryId,
              questionLogicId: logic.id,
            });
          }}
        >
          {translate("askQuestion")}
        </MenuItem>
      </StyledMenu>

      <EvidencesDrawer ref={evidencesDrawerRef} />

      <ConfirmDeleteLogicModal
        onDelete={() =>
          handleRemoveQuestionLogics({
            temporaryId: logic.temporaryId,
            logicId: logic.id,
          })
        }
        ref={deleteLogicModalRef}
      />

      <LogicQuestionsSection>
        {!!logic.questions?.length &&
          logic.questions.map((question) => (
            <LogicQuestions
              key={question.id || question.temporaryId}
              isOnViewMode={isOnViewMode}
              isChild={true}
              checklistQuestion={question}
            />
          ))}
      </LogicQuestionsSection>
    </>
  );
};
